import { isNonEmptyArray, notEmpty } from '@shared/utils/array';
import { useQuery } from '@tanstack/react-query';
import { ONE_HOUR } from '@/util/time';
import { useAccountSelector } from '../account/AccountProvider';
import { useUpdateAlarmCount } from '../alarm';
import {
  useBusinessEntityByIdQuery,
  useBusinessEntityQuery,
} from '../businessEntity/queries';
import { useCurrentUser } from '../user/queries';
import {
  EquipmentIncludeV1,
  EquipmentIncludeV2,
  fetchEquipmentByEntityIds,
  fetchEquipmentByIds,
  fetchEquipmentPermitsMe,
  fetchEquipmentSignalOffset,
  fetchExternalEquipment,
  fetchReportingTime,
} from './api';

export {
  useEquipmentByIdQuery,
  useEquipmentByIdsQuery,
  useEquipmentPermitsMeQuery,
  useEquipmentReportingTimeQuery,
  useEquipmentSignalOffsetsQuery,
  useExternalEquipmentQuery,
  useMyEquipment,
  useTop1000EquipmentQuery,
};

interface EquipmentByIdsQueryProps {
  equipmentIds?: (string | undefined)[];
  include?: EquipmentIncludeV1;
  enabled?: boolean;
}

/**
 * Get the first 1000 equipment.
 * Note! This is only supposed to be used in contexts (e.g. ecom) where we don't expect to have
 * users with access to more than 1000 equipment (i.e not for ATD).
 */
const useTop1000EquipmentQuery = () =>
  useBusinessEntityQuery<EquipmentEntity[]>(
    {
      type: 'equipment',
    },
    { size: 1000 },
    { queryKey: ['equipment', 'top1000'] },
    false
  );

function useMyEquipment(
  entityIds?: string[],
  include?: EquipmentIncludeV2,
  enabled = true
) {
  const user = useCurrentUser();
  const { account } = useAccountSelector();
  const { updateAlarmCountForEquipment } = useUpdateAlarmCount();
  const ids = isNonEmptyArray(entityIds)
    ? entityIds
    : account
      ? [account.id]
      : user.roles?.map((role) => role.entityId).filter(notEmpty);

  return useQuery({
    queryKey: ['myequipment', ids, include],
    queryFn: async () => {
      const equipmentList = await fetchEquipmentByEntityIds(ids, include);
      updateAlarmCountForEquipment(equipmentList);
      return equipmentList;
    },
    enabled: isNonEmptyArray(ids) && enabled,
    staleTime: ONE_HOUR,
  });
}

function useEquipmentByIdsQuery({
  equipmentIds,
  include,
  enabled = true,
}: EquipmentByIdsQueryProps) {
  const values = equipmentIds?.filter(notEmpty);

  return useQuery({
    queryKey: ['equipmentByIds', values, include],
    queryFn: () => fetchEquipmentByIds(values, include),
    enabled: isNonEmptyArray(values) && enabled,
    staleTime: ONE_HOUR,
  });
}

function useEquipmentByIdQuery(
  equipmentId?: string,
  include?: BusinessEntityInclude
) {
  return useBusinessEntityByIdQuery<EquipmentEntity>(equipmentId, include, {
    queryKey: ['equipmentId', equipmentId, include],
  });
}

function useEquipmentPermitsMeQuery(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ['equipmentPermitsMe', equipmentId],
    queryFn: () => fetchEquipmentPermitsMe(equipmentId),
    enabled: Boolean(equipmentId),
  });
}

function useEquipmentSignalOffsetsQuery(
  equipmentId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['signalOffset', equipmentId],
    queryFn: () => fetchEquipmentSignalOffset(equipmentId),
    enabled: Boolean(equipmentId) && enabled,
  });
}

function useEquipmentReportingTimeQuery(equipmentId?: string) {
  return useQuery({
    queryKey: ['reportingTime', equipmentId],
    queryFn: () => fetchReportingTime(equipmentId),
    enabled: !!equipmentId,
  });
}

function useExternalEquipmentQuery(serial?: string) {
  return useQuery({
    queryKey: ['externalEquipment', serial],
    queryFn: () => fetchExternalEquipment(serial),
    enabled: !!serial,
  });
}
